type Stringifiable = Parameters<typeof JSON.stringify>[0];
export interface Logger {
  info: typeof console.info;
  log: typeof console.log;
  error: typeof console.error;
  warn: typeof console.warn;
}

// Outbound frames carry user-generated content, so only their size may be logged.
// JSON.stringify throws on circular structures, and this measurement runs inside a
// log call on a send-failure path, where throwing would mask the original failure.
function payloadChars(message: Stringifiable): number | undefined {
  try {
    return JSON.stringify(message)?.length;
  } catch {
    return undefined;
  }
}

const PII_EVENTS = new Set([
  'broadcast-updated',
  'event-updated',
  'event-speakers-updated',
  'event-staffs-updated',
  'public-user-updated',
  'user-updated'
]);
const UNLOGGED_EVENTS = new Set(['posted', 'post_edited', 'post_deleted', 'post_migrated']);
const PII_KEYS = new Set(['first_name', 'last_name', 'firstname', 'lastname', 'email']);
const EVENT_NAME_PATTERN = /"event"\s*:\s*"([^"]+)"/;
const REDACTED = '[REDACTED]';

function redactPii(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(redactPii);
  }
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([key, entry]) => [
        key,
        PII_KEYS.has(key.toLowerCase()) && entry != null ? REDACTED : redactPii(entry)
      ])
    );
  }
  return value;
}

export function redactFrameForLog(data: unknown): unknown {
  if (typeof data !== 'string') {
    return data;
  }
  const eventName = EVENT_NAME_PATTERN.exec(data)?.[1];
  if (eventName && UNLOGGED_EVENTS.has(eventName)) {
    return null;
  }
  if (!eventName || !PII_EVENTS.has(eventName)) {
    return data;
  }
  try {
    return JSON.stringify(redactPii(JSON.parse(data)));
  } catch {
    return '[unparsable ' + eventName + ' frame, ' + data.length + ' chars]';
  }
}

function delay(attempt: number): number {
  if (attempt === 0) {
    return 0;
  }
  return Math.floor(2 ** (10 + attempt / 10));
}

export class SocketClient {
  socket: WebSocket | null = null;
  logname = '';
  reconnectTimerId: ReturnType<typeof setTimeout> = setTimeout(() => Function.prototype);
  reconnectionAttempt = 0;
  errorCount = 0;
  logger: Logger = console;
  url = '';
  openPromise: Promise<any> | null = null;
  // Bumped by disconnect() to invalidate any connect() call still awaiting preConnect().
  private connectionEpoch = 0;

  constructor(url: string, logger: Logger, logname: string) {
    this.url = url;
    this.logname = logname;
    this.logger = logger;
    // This should happen automatically but I see this present in mmsocket
    // and I don't want to cause some weird breakage.
    if (window.addEventListener) {
      window.addEventListener('beforeunload', () => {
        if (this.socket) {
          this.socket.close();
        }
      });
    }
  }

  _onOpened() {
    this.logger.info(this.logname + ': Socket opened');
    clearTimeout(this.reconnectTimerId);
    this.reconnectionAttempt = 0;
    this.errorCount = 0;
    if (this.socket) {
      this.socket.onclose = () => this._onClosed();
    } else {
      throw new Error('Cannot set onclosed for reconnection because there is no socket');
    }
    this.onOpened();
  }

  _onClosed() {
    this.logger.info(this.logname + ': Socket closed');
    if (this.socket) {
      this.socket.removeEventListener('error', this._onError);
      // this.socket.removeEventListener("message", this._onClosed);
      this.socket.onclose = null;
    }
    this.socket = null;
    this.openPromise = null;
    this.onClosed(this.reconnectionAttempt);
    this.reconnectionAttempt += 1;
    if (this.reconnectionAttempt < this.bailedStateThresold) {
      this.reconnectTimerId = setTimeout(() => {
        this.connect();
      }, delay(this.reconnectionAttempt));
    } else {
      clearTimeout(this.reconnectTimerId);
      this.reconnectionAttempt = 0;
      this.disconnect();
      this.onBail();
      this.logger.info(this.logname + ': Bailing on trying to reconnect the socket');
    }
  }

  _onError() {
    this.logger.warn(this.logname + ': socket has errored out!');
    this.onError(this.errorCount);
    clearTimeout(this.reconnectTimerId);
    if (this.socket) {
      this.socket.removeEventListener('error', this._onError);
      // this.socket.removeEventListener("message", this._onClosed);
      this.socket.onclose = null;
      this.socket.close();
    } else {
      this.logger.warn(this.logname + ': Cannot close socket as its null');
    }
    this.socket = null;
    this.openPromise = null;
    this.reconnectionAttempt += 1;
    this.errorCount += 1;
    return new Promise((resolve, reject) => {
      if (this.reconnectionAttempt < this.bailedStateThresold) {
        this.reconnectTimerId = setTimeout(() => {
          this.connect().then(resolve).catch(reject);
        }, delay(this.reconnectionAttempt));
      } else {
        clearTimeout(this.reconnectTimerId);
        this.reconnectionAttempt = 0;
        this.disconnect();
        this.onBail();
        this.logger.info(this.logname + ': Bailing on trying to reconnect the socket');
      }
    });
  }

  _onMessage(event: any) {
    const loggableFrame = redactFrameForLog(event?.data);
    if (loggableFrame !== null) {
      this.logger.info(this.logname + ' message: ' + loggableFrame);
    }
    this.onMessage(event);
  }

  connect(): Promise<any> {
    this.logger.info(this.logname + ': Starting socket connection for: ' + this.logname);
    const epochAtConnect = this.connectionEpoch;
    this.openPromise = this.preConnect()
      .then(() => {
        if (epochAtConnect !== this.connectionEpoch) {
          this.logger.info(this.logname + ': Aborting connect, disconnect() was called during preConnect');
          return;
        }
        this.socket = new WebSocket(this.url);
        this.logger.info('Call made for opening websocket to: ' + this.logname + ' at ' + this.url);
        (window as any)[this.logname] = this;

        this.socket.addEventListener('message', this._onMessage.bind(this));
        return new Promise((resolve, reject) => {
          if (this.socket) {
            this.socket.addEventListener('open', event => {
              resolve(event);
              this._onOpened();
            });
            this.socket.addEventListener('error', err => {
              this.logger.error('SocketClient: WebSocket connection error', { error: err });
              this._onError().then(resolve);
            });
          } else {
            reject(new Error('Cannot attach open and error event handlers on socket as its null!'));
          }
        });
      })
      .catch(err => {
        this.logger.warn(this.logname + ': Error during opening socket for ', this.logname);
        this.logger.warn(this.logname + ': Retrying connection');
        if (err !== 'NO_RETRY_IN_CATCH') {
          return this.connect();
        }
      });
    return this.openPromise;
  }

  // Close the socket and do not try to reconnect
  disconnect() {
    this.connectionEpoch += 1;
    clearTimeout(this.reconnectTimerId);
    if (this.socket) {
      this.socket.onclose = null;
      this.socket.close();
      this.socket = null;
      this.openPromise = null;
    }
  }
  // Close the socket to immitate what happens when socket is dropped to test reconnection
  close() {
    this.socket?.close();
  }
  sendMessage(message: Stringifiable) {
    if (this.openPromise !== null) {
      return this.openPromise.then(() => {
        if (this.socket && this.socket.readyState === WebSocket.OPEN) {
          this.socket.send(JSON.stringify(message));
        } else {
          this.logger.warn('Cannot send socket message, socket is not open', {
            channel: this.logname,
            chars: payloadChars(message)
          });
        }
      });
    }
    return new Promise((resolve, reject) => {
      this.logger.warn(this.logname + ': Sending socket message failed because it was not open! Retrying in 5 sec.');
      setTimeout(() => {
        this.sendMessage(message).then(resolve).catch(reject);
      }, 5000);
    });
  }

  get bailedStateThresold() {
    return 20;
  }

  // To be overridden by concrete classes
  preConnect() {
    return Promise.resolve();
  }

  // To be implemented by concrete classes
  //eslint-disable-next-line
  onError(reconnectionAttempt: number) {
    throw new Error('onError not implemented');
  }

  //eslint-disable-next-line
  onMessage(event: any) {
    throw new Error('onMessage not implemented');
  }

  //eslint-disable-next-line
  onClosed(reconnectionAttempt: number) {
    throw new Error('onClose not implemented');
  }

  onOpened() {
    throw new Error('onOpened not implemented');
  }

  onBail() {
    throw new Error('onBail not implemented');
  }
}
